import { PropsWithChildren, useEffect, useRef, useState } from "react";
import classNames from "classnames/bind";
import { ChevronLeftOutlined as ChevronLeft } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronLeftOutlined";
import { ChevronRightOutlined as ChevronRight } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronRightOutlined";
import { GalleryVideoElement } from "../GalleryVideoElement/GalleryVideoElement";
import styles from "./Carousel.module.scss";
import { Thumbnails } from "../Thumbnails/Thumbnails";
import { Pips } from "@/components/Galleries/components/Pips/Pips";
import { dispatchGalleryMonetateQEvent } from "../Analytics/dispatchGalleryMonetateQEvent";
import { useRouter } from "next/router";
import {
  GalleryObject,
  VideoPreloadValue,
  SocialProofMessagesProperties,
} from "@/components/Galleries/utils/utils.ts";
import { GalleryImageElement } from "../GalleryImageElement/GalleryImageElement";
import { useStandardProps } from "@/context/StandardPropsContext/StandardPropsContext";
import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId";

const cx = classNames.bind(styles);

type CarouselItemProps = {
  mediaItem: GalleryObject;
  isSelected: boolean;
  index: number;
  fullscreen?: boolean;
  imageSizes?: string;
  intersectionObserver: IntersectionObserver | null;
  hasSuperZoom?: boolean;
  usePrefetching?: boolean;
  videoPreload: VideoPreloadValue;
  carouselGalleryInpTest?: boolean;
  condensedGallery: boolean;
  videoAutoplay?: boolean;
  setVideoAutoplay: (arg: boolean) => void;
  showZoomIconOnHover?: boolean;
};

type CarouselProps = {
  mediaItems: GalleryObject[];
  selectedInd: number;
  setSelectedInd: (arg: number) => void;
  dataComponent?: string;
  fullscreen?: boolean;
  initialised?: boolean;
  imageSizes?: string;
  hasImmediateStateUpdates?: boolean;
  hasTransparentThumbnails?: boolean;
  hasDeferredMonetateQEvents?: boolean;
  hasDeferredIntersectionObserverCallback?: boolean;
  hasSuperZoom?: boolean;
  usePrefetching?: boolean;
  useResponsiveImages?: boolean;
  carouselGalleryInpTest?: boolean;
  videoPreload: VideoPreloadValue;
  condensedGallery?: boolean;
  onClick?: () => void;
  showZoomIconOnHover?: boolean;
  videoAutoplayOnLoad?: boolean;
  socialProofMessagesProperties?: SocialProofMessagesProperties;
};

function scrollIntoView(container: HTMLElement, index: number) {
  const target = container.children[index];
  if (!(target instanceof HTMLElement)) return;
  const targetRect = target.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const targetScrollXPos = target.offsetLeft;
  const targetScrollYPos =
    target.offsetTop - (containerRect.height - targetRect.height) / 2;

  container.scrollTo(targetScrollXPos, targetScrollYPos);
}

const CarouselItem = ({
  mediaItem,
  index,
  fullscreen,
  imageSizes,
  intersectionObserver,
  isSelected,
  hasSuperZoom,
  usePrefetching,
  videoPreload,
  carouselGalleryInpTest = false,
  condensedGallery,
  videoAutoplay = false,
  setVideoAutoplay,
  showZoomIconOnHover = false,
}: CarouselItemProps) => {
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (itemRef.current && intersectionObserver) {
      intersectionObserver.observe(itemRef.current);
    }

    return () => {
      if (itemRef.current && intersectionObserver) {
        intersectionObserver.unobserve(itemRef.current);
      }
    };
  }, [intersectionObserver]);

  if (!mediaItem) {
    return null;
  }

  return (
    <div
      ref={itemRef}
      key={mediaItem.imageUrl}
      className={cx(styles.galleryItem, styles.galleryItemPeek, {
        condensedGallery,
      })}
    >
      {mediaItem.type === "video" ? (
        <GalleryVideoElement
          className={styles.galleryImage}
          key={`galleryobjectkey-${index}`}
          video={mediaItem}
          videoId={index.toString()}
          isInView={isSelected}
          isModal={fullscreen}
          videoPreload={videoPreload}
          carouselGalleryInpTest={carouselGalleryInpTest}
          videoAutoplay={videoAutoplay}
          setVideoAutoplay={setVideoAutoplay}
          showZoomIconOnHover={showZoomIconOnHover}
        />
      ) : (
        <GalleryImageElement
          fullscreen={fullscreen}
          mediaItem={mediaItem}
          isSelected={isSelected}
          index={index}
          hasSuperZoom={hasSuperZoom}
          sizes={imageSizes}
          usePrefetching={usePrefetching}
          carouselGalleryInpTest={carouselGalleryInpTest}
        />
      )}
    </div>
  );
};

const generateMediaItemsKey = (mediaItems: GalleryObject[]) =>
  mediaItems.map((item) => item.thumbnailUrl).join("|");

export const Carousel = ({
  mediaItems,
  selectedInd,
  setSelectedInd,
  dataComponent,
  children,
  fullscreen = false,
  initialised = true,
  imageSizes = "100vw",
  hasImmediateStateUpdates,
  hasTransparentThumbnails,
  hasDeferredMonetateQEvents,
  hasDeferredIntersectionObserverCallback,
  hasSuperZoom,
  usePrefetching,
  useResponsiveImages,
  carouselGalleryInpTest = false,
  videoPreload,
  condensedGallery = false,
  onClick,
  showZoomIconOnHover,
  videoAutoplayOnLoad = false,
  socialProofMessagesProperties,
}: PropsWithChildren<CarouselProps>) => {
  const router = useRouter();
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const thumbsContainerRef = useRef<HTMLDivElement>(null);
  const [fullscreenAnimationEnded, setfullscreenAnimationEnded] =
    useState(false);
  const mediaItemRef = useRef(generateMediaItemsKey(mediaItems));
  const [loading, setLoading] = useState(false);
  const [intersectionObserver, setIntersectionObserver] =
    useState<IntersectionObserver | null>(null);
  const [videoAutoplay, setVideoAutoplay] = useState(videoAutoplayOnLoad);
  const { flags } = useStandardProps();
  const { monetateId } = useMonetateId();
  const isReduceSPMPersistenceScrollEnabled =
    flags.reduceSocialProofMessagesPersistenceScroll?.value === true;

  const isCarouselReady = fullscreenAnimationEnded === fullscreen;
  const isCurrentItemClickable = !!onClick;

  const modalMode =
    mediaItems[0]?.type === "video" ? "videoModal" : "imageModal";

  const scrollToIndex = (index: number) => {
    if (index < 0 || index >= mediaItems.length || !mainContainerRef.current) {
      return;
    }
    scrollIntoView(mainContainerRef.current, index);
  };

  const handleFullscreenAnimationEnd = () => {
    setfullscreenAnimationEnded(true);
    scrollToIndex(selectedInd);
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    const intersectingEntry = entries.find(
      ({ isIntersecting }) => isIntersecting,
    );

    if (mainContainerRef.current && intersectingEntry) {
      const intersectionIndex = [...mainContainerRef.current.children].indexOf(
        intersectingEntry.target,
      );

      dispatchGalleryMonetateQEvent(
        "CarouselInteraction",
        fullscreen ? modalMode : "generic",
        hasDeferredMonetateQEvents,
        carouselGalleryInpTest,
      );

      if (socialProofMessagesProperties && intersectionIndex > 0) {
        recordImpressionByParam(
          socialProofMessagesProperties.monetateEnvironment,
          socialProofMessagesProperties.experiencesRawObject,
          "pdp-scaffold-carouselImageGalleryScroll",
          "true",
          monetateId,
        );
      }

      if (
        isReduceSPMPersistenceScrollEnabled &&
        socialProofMessagesProperties?.setFadeOutSocialProofMessages &&
        intersectionIndex > 0
      ) {
        socialProofMessagesProperties.setFadeOutSocialProofMessages(true);
      }

      if (hasDeferredIntersectionObserverCallback) {
        setTimeout(() => {
          setSelectedInd(intersectionIndex);
        }, 1000);
      } else {
        setSelectedInd(intersectionIndex);
      }
    }
  };

  const handleScrollIntoView = () => {
    if (mainContainerRef.current) {
      mainContainerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const showPips = mediaItems.length > 1 && !fullscreen;

  useEffect(() => {
    const modalElement = mainContainerRef.current?.closest(
      '[data-testid="modal"]',
    );

    if (fullscreen) {
      modalElement?.addEventListener(
        "animationstart",
        handleFullscreenAnimationEnd,
        {
          once: true,
        },
      );
    }

    document.addEventListener(
      "carousel-gallery-scroll-into-view",
      handleScrollIntoView,
    );

    return () => {
      modalElement?.removeEventListener(
        "animationstart",
        handleFullscreenAnimationEnd,
      );

      document.removeEventListener(
        "carousel-gallery-scroll-into-view",
        handleScrollIntoView,
      );
    };
  }, []);

  useEffect(() => {
    let observer: IntersectionObserver;
    if (isCarouselReady) {
      observer = new IntersectionObserver(handleIntersection, {
        root: mainContainerRef.current,
        threshold: 0.5,
      });

      setIntersectionObserver(observer);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [isCarouselReady]);

  useEffect(() => {
    if (thumbsContainerRef.current && isCarouselReady) {
      scrollIntoView(thumbsContainerRef.current, selectedInd);
    }
  }, [selectedInd, isCarouselReady]);

  useEffect(() => {
    if (generateMediaItemsKey(mediaItems) !== mediaItemRef.current) {
      mainContainerRef.current?.scrollTo(0, 0);
      mediaItemRef.current = generateMediaItemsKey(mediaItems);
    }
  }, [mediaItems]);

  useEffect(() => {
    const handleRouteChange = () => {
      setLoading(true);
    };

    const handleRouteChangeComplete = () => {
      setLoading(false);
    };

    router.events.on("routeChangeStart", handleRouteChange);
    router.events.on("routeChangeComplete", handleRouteChangeComplete);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
  }, [router]);

  const handlePrevClick = () => {
    scrollToIndex(selectedInd - 1);
    if (selectedInd > 0 && hasImmediateStateUpdates) {
      setSelectedInd(selectedInd - 1);
    }
    dispatchGalleryMonetateQEvent(
      "LeftArrow",
      fullscreen ? modalMode : "generic",
      hasDeferredMonetateQEvents,
      carouselGalleryInpTest,
    );
  };

  const handleNextClick = () => {
    scrollToIndex(selectedInd + 1);
    if (selectedInd < mediaItems.length - 1 && hasImmediateStateUpdates) {
      setSelectedInd(selectedInd + 1);
    }
    dispatchGalleryMonetateQEvent(
      "RightArrow",
      fullscreen ? modalMode : "generic",
      hasDeferredMonetateQEvents,
      carouselGalleryInpTest,
    );
  };

  return (
    <div
      className={cx(styles.wrapper, {
        "wrapper--fullscreen": fullscreen,
      })}
      data-testid={fullscreen ? "gallery:zoom:modal" : "slot:header"}
      data-component={dataComponent}
    >
      <div
        className={cx(styles.main, {
          "main--fullscreen": fullscreen,
          "main--singleItem": mediaItems.length < 2,
          "main--condensed": condensedGallery,
        })}
      >
        <div
          data-testid="carousel-main-container"
          className={cx(styles.gallery, {
            "gallery--initialised": initialised,
            "gallery--loading": !isCarouselReady || loading,
            "gallery--button": isCurrentItemClickable,
            "gallery--zoom-on-hover": showZoomIconOnHover,
          })}
          ref={mainContainerRef}
          onClick={isCurrentItemClickable ? onClick : undefined}
          role={isCurrentItemClickable ? "button" : undefined}
          aria-label={isCurrentItemClickable ? "Zoom image" : undefined}
          tabIndex={isCurrentItemClickable ? 0 : undefined}
        >
          {mediaItems.map((mediaItem, ind) => (
            <CarouselItem
              mediaItem={mediaItem}
              intersectionObserver={intersectionObserver}
              fullscreen={fullscreen}
              index={ind}
              isSelected={selectedInd === ind}
              key={`${mediaItem.imageUrl}-${ind}`}
              imageSizes={imageSizes}
              hasSuperZoom={hasSuperZoom}
              usePrefetching={usePrefetching}
              videoPreload={videoPreload}
              carouselGalleryInpTest={carouselGalleryInpTest}
              condensedGallery={condensedGallery}
              videoAutoplay={videoAutoplay}
              setVideoAutoplay={setVideoAutoplay}
              showZoomIconOnHover={showZoomIconOnHover}
            />
          ))}
        </div>
        <button
          className={styles.buttonPrev}
          type="button"
          onClick={handlePrevClick}
          data-testid="standard:gallery:navigation:left"
          disabled={selectedInd < 1}
          aria-label={`Show image ${selectedInd > 0 ? selectedInd : 1} of ${mediaItems.length}`}
        >
          <ChevronLeft size="medium" />
        </button>
        <button
          className={styles.buttonNext}
          type="button"
          onClick={handleNextClick}
          data-testid="standard:gallery:navigation:right"
          disabled={selectedInd >= mediaItems.length - 1}
          aria-label={`Show image ${selectedInd < mediaItems.length - 1 ? selectedInd + 2 : mediaItems.length} of ${mediaItems.length}`}
        >
          <ChevronRight size="medium" />
        </button>
        {children}
      </div>
      {!condensedGallery && mediaItems.length > 1 && (
        <Thumbnails
          media={mediaItems}
          ref={thumbsContainerRef}
          scrollToIndex={scrollToIndex}
          selectedInd={selectedInd}
          className={styles.thumbnails}
          fullscreen={fullscreen}
          transparent={hasTransparentThumbnails}
          imageSizes={imageSizes}
          useResponsiveImages={useResponsiveImages}
          carouselGalleryInpTest={carouselGalleryInpTest}
        />
      )}
      {showPips && (
        <Pips
          media={mediaItems}
          selectedIndex={selectedInd}
          condensedGallery={condensedGallery}
        />
      )}
      <div
        className={styles.visuallyHidden}
        aria-live="polite"
        aria-atomic="true"
      >
        {`Viewing image ${selectedInd + 1} of ${mediaItems.length}`}
      </div>
    </div>
  );
};
